import type { UserCustomerMismatchFragment } from "@/v2-backoffice-client/user/customerMismatch/__types__/user.customerMismatch.fragment"

/**
 * What explains a cross-customer membership, read off how many of the
 * team's live members the team's own customer owns, or what HubSpot did to
 * the companies. The labels are what the board's Likely Fix column, pills
 * and grouping show.
 */
export const MISMATCH_FIX_MERGED = "Merged in HubSpot"
export const MISMATCH_FIX_TEAM = "Team linked wrong"
export const MISMATCH_FIX_USER = "User owned wrong"
export const MISMATCH_FIX_MIXED = "Mixed team"

export type MismatchLikelyFix =
  | typeof MISMATCH_FIX_MERGED
  | typeof MISMATCH_FIX_TEAM
  | typeof MISMATCH_FIX_USER
  | typeof MISMATCH_FIX_MIXED

type MismatchCounts = Pick<
  UserCustomerMismatchFragment,
  "TeamMemberCount" | "TeamMembersOwnedByTeamCustomer"
>

type MismatchMergeFacts = Pick<
  UserCustomerMismatchFragment,
  | "UserCustomerSurvivingExternalCompanyId"
  | "TeamCustomerSurvivingExternalCompanyId"
>

/** Whether HubSpot merged away the company of either side's customer. */
export function isMismatchMerged(row: MismatchMergeFacts) {
  return Boolean(
    row.UserCustomerSurvivingExternalCompanyId ||
    row.TeamCustomerSurvivingExternalCompanyId,
  )
}

/**
 * A side whose company HubSpot merged away outranks the member counts: the
 * two customers are two rows for one company, and neither of them is the
 * right one. Otherwise, a team whose customer owns none of its members is
 * the team that is linked wrong (the Fagerstad Utvikling team sat under
 * Trollfjord / Hadsel this way for 22 months); a team whose customer owns
 * every other member points at this one user; anything in between is a team
 * with members from several customers, which an operator has to read before
 * fixing.
 */
export function getMismatchLikelyFix(
  row: MismatchCounts & MismatchMergeFacts,
): MismatchLikelyFix {
  if (isMismatchMerged(row)) return MISMATCH_FIX_MERGED
  return getMismatchMemberFix(row)
}

/** The member-count reading alone, merges aside. */
function getMismatchMemberFix(row: MismatchCounts) {
  if (row.TeamMembersOwnedByTeamCustomer === 0) return MISMATCH_FIX_TEAM
  if (row.TeamMembersOwnedByTeamCustomer >= row.TeamMemberCount - 1) {
    return MISMATCH_FIX_USER
  }
  return MISMATCH_FIX_MIXED
}

/** "Fagerstad Utvikling AS", or the HubSpot id for a customer without a name. */
export function formatMismatchCustomerName(
  name: string | null | undefined,
  externalId: string | null | undefined,
) {
  return name ?? externalId ?? "Unnamed"
}

/** A customer a row's action can move the user or the team to. */
export type MismatchTarget = {
  Id: number
  Name: string
  /** The legacy HubSpot id every ownership writer links through; null = a team cannot be moved to it yet. */
  ExternalCustomerId: string | null
}

export type MismatchSide = "user" | "team"

/**
 * The customer a side stands for once HubSpot's merges are read through:
 * the side's own customer while the CRM still has its company, the
 * survivor's customer when the company was merged away, and null when it
 * was merged away but the sync has not linked the survivor to a customer
 * yet (within the hour) - nothing can be moved to that side then.
 */
export function getMismatchSideTarget(
  row: UserCustomerMismatchFragment,
  side: MismatchSide,
): MismatchTarget | null {
  const merged =
    side === "user"
      ? row.UserCustomerSurvivingExternalCompanyId
      : row.TeamCustomerSurvivingExternalCompanyId
  if (merged) {
    const survivor =
      side === "user"
        ? row.UserCustomerSurvivingCustomer
        : row.TeamCustomerSurvivingCustomer
    if (!survivor) return null
    return {
      Id: survivor.Id,
      Name: formatMismatchCustomerName(
        survivor.Name,
        survivor.ExternalCustomerId,
      ),
      ExternalCustomerId: survivor.ExternalCustomerId ?? null,
    }
  }
  if (side === "user") {
    return {
      Id: row.UserCustomerId,
      Name: formatMismatchCustomerName(
        row.UserCustomerName,
        row.UserCustomerExternalId,
      ),
      ExternalCustomerId: row.UserCustomerExternalId ?? null,
    }
  }
  return {
    Id: row.TeamCustomerId,
    Name: formatMismatchCustomerName(
      row.TeamCustomerName,
      row.TeamCustomerExternalId,
    ),
    ExternalCustomerId: row.TeamCustomerExternalId ?? null,
  }
}

/** One of the two ways a row is put right: what moves, and where to. */
export type MismatchChoice = {
  /** Which side moves: the user (to where the team belongs) or the team, every member of it (to where the user belongs). */
  moves: MismatchSide
  target: MismatchTarget
  /** The choice the team's other members point at; at most one of the two. */
  isSuggested: boolean
  /** Why the choice cannot be taken right now (a team cannot move to a customer without a HubSpot link). */
  disabledReason?: string
}

/**
 * How a row is put right. Every mismatch has two sides that stand for two
 * customers, and two ways out: move the user to where the team belongs, or
 * move the team (all its members) to where the user belongs. When HubSpot
 * merged both customers' companies into one, the two sides stand for the
 * same customer and there is one way: move whatever is not on it there.
 * While a merged-away side's survivor has no customer yet (the sync links
 * it within the hour), nothing can move.
 */
export type MismatchChoices =
  | { kind: "pending"; survivingExternalCompanyId: string }
  | {
      kind: "single"
      target: MismatchTarget
      /** Which sides still have to move onto the target. */
      moveUser: boolean
      moveTeam: boolean
      /** The customers HubSpot merged into the target, for the wording. */
      mergedNames: string[]
    }
  | { kind: "two"; user: MismatchChoice; team: MismatchChoice }

export function getMismatchChoices(
  row: UserCustomerMismatchFragment,
): MismatchChoices {
  const userTarget = getMismatchSideTarget(row, "user")
  const teamTarget = getMismatchSideTarget(row, "team")

  if (!userTarget || !teamTarget) {
    const pendingId = !userTarget
      ? row.UserCustomerSurvivingExternalCompanyId
      : row.TeamCustomerSurvivingExternalCompanyId
    return { kind: "pending", survivingExternalCompanyId: pendingId ?? "" }
  }

  if (userTarget.Id === teamTarget.Id) {
    const mergedNames: string[] = []
    if (row.UserCustomerSurvivingExternalCompanyId) {
      mergedNames.push(
        formatMismatchCustomerName(
          row.UserCustomerName,
          row.UserCustomerExternalId,
        ),
      )
    }
    if (row.TeamCustomerSurvivingExternalCompanyId) {
      mergedNames.push(
        formatMismatchCustomerName(
          row.TeamCustomerName,
          row.TeamCustomerExternalId,
        ),
      )
    }
    return {
      kind: "single",
      target: userTarget,
      moveUser: row.UserCustomerId !== userTarget.Id,
      moveTeam: row.TeamCustomerId !== userTarget.Id,
      mergedNames: [...new Set(mergedNames)],
    }
  }

  const memberFix = getMismatchMemberFix(row)
  return {
    kind: "two",
    user: {
      moves: "user",
      target: teamTarget,
      isSuggested: memberFix === MISMATCH_FIX_USER,
    },
    team: {
      moves: "team",
      target: userTarget,
      isSuggested: memberFix === MISMATCH_FIX_TEAM,
      disabledReason: userTarget.ExternalCustomerId
        ? undefined
        : `${userTarget.Name} is not linked to a HubSpot company, so a team cannot be moved to it yet. Link it on the customer's panel first.`,
    },
  }
}

/**
 * The row's choices as one line, for the export and the column's text
 * value: "Both → Bryn Eiendom AS", "User → X (suggested) or Team → Y".
 */
export function describeMismatchChoices(row: UserCustomerMismatchFragment) {
  const choices = getMismatchChoices(row)
  if (choices.kind === "pending") {
    return `Waiting for the sync (company ${choices.survivingExternalCompanyId})`
  }
  if (choices.kind === "single") {
    const label =
      choices.moveUser && choices.moveTeam
        ? "Both"
        : choices.moveTeam
          ? "Team"
          : "User"
    return `${label} → ${choices.target.Name}`
  }
  const describe = (choice: MismatchChoice) =>
    `${choice.moves === "user" ? "User" : "Team"} → ${choice.target.Name}${choice.isSuggested ? " (suggested)" : ""}`
  return `${describe(choices.user)} or ${describe(choices.team)}`
}

/**
 * How a bulk action reads the selected rows: the suggested choice of each
 * (what the row's highlighted button would do), every user to where its
 * team belongs, or every team to where its user belongs. A row whose two
 * sides stand for one customer has no direction to choose and moves the
 * same way under every strategy.
 */
export type MismatchPlanStrategy =
  | "suggested"
  | "users-to-teams"
  | "teams-to-users"

export type MismatchPlanMove<TIds extends "tenantIds" | "userIds"> = {
  target: MismatchTarget
} & Record<TIds, number[]> & { memberCount: number }

export type MismatchPlan = {
  /** Teams to relink, grouped per target customer; memberCount is how many people ride along. */
  teamMoves: MismatchPlanMove<"tenantIds">[]
  /** Users to move, grouped per target customer. */
  userMoves: MismatchPlanMove<"userIds">[]
  /** Rows the plan leaves alone, by reason, in the order they were met. */
  skipped: { reason: string; count: number }[]
  /** Rows the plan puts right. */
  resolvedRows: number
  /** Rows whose two sides HubSpot merged into one customer (moved whole under every strategy). */
  mergedRows: number
}

// Skip reasons are written to follow "N rows are left out:" and a count:
// "3 in a mixed team, …".
const MISMATCH_SKIP_PENDING =
  "waiting for the sync to link the surviving company to a customer (within the hour)"
const MISMATCH_SKIP_MIXED =
  "in a mixed team, where no choice is suggested (use the row's buttons)"
const MISMATCH_SKIP_UNLINKED_TARGET =
  "with a user's customer that has no HubSpot link, so the team cannot move to it"
const MISMATCH_SKIP_AMBIGUOUS_TEAM =
  "with a team the selection sends two ways, its selected members being owned by different customers (use the row's buttons)"
const MISMATCH_SKIP_AMBIGUOUS_USER =
  "with a user the selection sends two ways, being selected in teams of different customers (use the row's buttons)"

/**
 * Turns the selected rows into the writes a bulk action makes: one team
 * relink per target customer, one user move per target customer. Teams
 * are planned before users, so a user always lands on the customer of the
 * team they sit in. A team or user that would be sent two different ways by
 * the selection is left alone and counted, never moved by the first row met.
 */
export function buildMismatchPlan(
  rows: UserCustomerMismatchFragment[],
  strategy: MismatchPlanStrategy,
): MismatchPlan {
  const teamTargets = new Map<
    number,
    { target: MismatchTarget; memberCount: number }
  >()
  const userTargets = new Map<number, MismatchTarget>()
  const conflictingTeams = new Set<number>()
  const conflictingUsers = new Set<number>()
  const skippedByReason = new Map<string, number>()
  let resolvedRows = 0
  let mergedRows = 0

  function skip(reason: string) {
    skippedByReason.set(reason, (skippedByReason.get(reason) ?? 0) + 1)
  }

  function planTeam(row: UserCustomerMismatchFragment, target: MismatchTarget) {
    const existing = teamTargets.get(row.TenantId)
    if (existing && existing.target.Id !== target.Id) {
      conflictingTeams.add(row.TenantId)
      return
    }
    teamTargets.set(row.TenantId, { target, memberCount: row.TeamMemberCount })
  }

  function planUser(row: UserCustomerMismatchFragment, target: MismatchTarget) {
    const existing = userTargets.get(row.UserId)
    if (existing && existing.Id !== target.Id) {
      conflictingUsers.add(row.UserId)
      return
    }
    userTargets.set(row.UserId, target)
  }

  // Rows whose moves were planned, kept so a conflict found on a later row
  // can take the earlier one off the count too.
  const plannedRows: {
    row: UserCustomerMismatchFragment
    team: boolean
    user: boolean
  }[] = []

  for (const row of rows) {
    const choices = getMismatchChoices(row)

    if (choices.kind === "pending") {
      skip(MISMATCH_SKIP_PENDING)
      continue
    }

    if (choices.kind === "single") {
      mergedRows += 1
      if (choices.moveTeam) planTeam(row, choices.target)
      if (choices.moveUser) planUser(row, choices.target)
      plannedRows.push({ row, team: choices.moveTeam, user: choices.moveUser })
      continue
    }

    const choice =
      strategy === "users-to-teams"
        ? choices.user
        : strategy === "teams-to-users"
          ? choices.team
          : choices.user.isSuggested
            ? choices.user
            : choices.team.isSuggested
              ? choices.team
              : null
    if (!choice) {
      skip(MISMATCH_SKIP_MIXED)
      continue
    }
    if (choice.moves === "team") {
      if (!choice.target.ExternalCustomerId) {
        skip(MISMATCH_SKIP_UNLINKED_TARGET)
        continue
      }
      planTeam(row, choice.target)
      plannedRows.push({ row, team: true, user: false })
    } else {
      planUser(row, choice.target)
      plannedRows.push({ row, team: false, user: true })
    }
  }

  for (const id of conflictingTeams) teamTargets.delete(id)
  for (const id of conflictingUsers) userTargets.delete(id)

  for (const planned of plannedRows) {
    const teamConflict =
      planned.team && conflictingTeams.has(planned.row.TenantId)
    const userConflict =
      planned.user && conflictingUsers.has(planned.row.UserId)
    if (teamConflict) {
      skip(MISMATCH_SKIP_AMBIGUOUS_TEAM)
      continue
    }
    if (userConflict) {
      skip(MISMATCH_SKIP_AMBIGUOUS_USER)
      continue
    }
    resolvedRows += 1
  }

  const teamMoves = new Map<number, MismatchPlanMove<"tenantIds">>()
  for (const [tenantId, { target, memberCount }] of teamTargets) {
    const move = teamMoves.get(target.Id) ?? {
      target,
      tenantIds: [],
      memberCount: 0,
    }
    move.tenantIds.push(tenantId)
    move.memberCount += memberCount
    teamMoves.set(target.Id, move)
  }
  const userMoves = new Map<number, MismatchPlanMove<"userIds">>()
  for (const [userId, target] of userTargets) {
    const move = userMoves.get(target.Id) ?? {
      target,
      userIds: [],
      memberCount: 0,
    }
    move.userIds.push(userId)
    move.memberCount += 1
    userMoves.set(target.Id, move)
  }

  return {
    teamMoves: [...teamMoves.values()],
    userMoves: [...userMoves.values()],
    skipped: [...skippedByReason].map(([reason, count]) => ({ reason, count })),
    resolvedRows,
    mergedRows,
  }
}

/** "3 teams (61 members) and 12 users", for the bulk confirmations. */
export function describeMismatchPlanMoves(plan: MismatchPlan) {
  const teamCount = plan.teamMoves.reduce(
    (sum, move) => sum + move.tenantIds.length,
    0,
  )
  const memberCount = plan.teamMoves.reduce(
    (sum, move) => sum + move.memberCount,
    0,
  )
  const userCount = plan.userMoves.reduce(
    (sum, move) => sum + move.userIds.length,
    0,
  )
  const parts: string[] = []
  if (teamCount > 0) {
    parts.push(
      `${teamCount} team${teamCount === 1 ? "" : "s"} (${memberCount} member${memberCount === 1 ? "" : "s"})`,
    )
  }
  if (userCount > 0)
    parts.push(`${userCount} user${userCount === 1 ? "" : "s"}`)
  return parts.join(" and ")
}

/** The target customers of a plan as one phrase: "Bryn Eiendom AS", or "4 customers". */
export function describeMismatchPlanTargets(plan: MismatchPlan) {
  const names = new Set([
    ...plan.teamMoves.map((move) => move.target.Name),
    ...plan.userMoves.map((move) => move.target.Name),
  ])
  if (names.size === 1) return [...names][0]
  return `${names.size} customers`
}

/** " 2 rows are left out: …", or "" when nothing is. */
export function describeMismatchPlanSkips(plan: MismatchPlan) {
  if (plan.skipped.length === 0) return ""
  const total = plan.skipped.reduce((sum, entry) => sum + entry.count, 0)
  const reasons =
    plan.skipped.length === 1
      ? plan.skipped[0].reason
      : plan.skipped.map((entry) => `${entry.count} ${entry.reason}`).join("; ")
  return ` ${total} row${total === 1 ? " is" : "s are"} left out: ${reasons}.`
}
